import React from 'react';
import { Column } from 'lib/src/types/table';
import TablePagination from 'lib/src/components/table/TablePagination';
import TableSearch from 'lib/src/components/table/TableSearch';
import useTable from 'lib/src/components/table/useTable';
import { AssessmentSummary } from 'src/types/shared/Assessments';
import ScoreSummaryExtraRows from '@pages/challengeGroups/challenge/challengeScoreSummary/ScoreSummaryExtraRows';

const ScoreSummaryTable = <TRow extends CustomRow>({
    columns = [],
    rows = [],
    isLoading = false,
    pageSizes = [10, 25, 100],
    noDataMessage = 'There is no data to display',
    currentRow,
}: ScoreSummaryTableProps<TRow>): JSX.Element => {
    const {
        paginatedRows,
        handleSortClick,
        sortColumn,
        sortAsc,
        page,
        setPage,
        maxPage,
        pageSizeOpts,
        pageSize,
        setPageSize,
        paginationDescription,
        searchTerm,
        setSearchTerm,
        canSearch,
    } = useTable<TRow>({
        columns,
        rows,
        pageSizes,
    });

    const isEmpty = !rows.length;

    return (
        <>
            {canSearch && !isEmpty && <TableSearch value={searchTerm} onChange={setSearchTerm} />}
            <table className="score-summary-table">
                <thead>
                    <tr>{columns.map(renderHeading)}</tr>
                </thead>
                <tbody>
                    {isLoading && (
                        <tr>
                            <td className="loading-row" colSpan={columns.length}>
                                <i className="fal fa-spinner fa-spin"></i>
                            </td>
                        </tr>
                    )}
                    {isEmpty && !isLoading && (
                        <tr className="no-data-row">
                            <td colSpan={columns.length}>{noDataMessage}</td>
                        </tr>
                    )}
                    {paginatedRows.map(row => {
                        return (
                            <React.Fragment key={row.key || row.id}>
                                <tr
                                    className={
                                        !!currentRow && currentRow !== row.id ? 'blocked' : ''
                                    }
                                >
                                    {columns.map(col => (
                                        <td key={col.key}>{col.getValue(row)}</td>
                                    ))}
                                    {!!currentRow && currentRow !== row.id && (
                                        <div className="semitransprent-blocker" />
                                    )}
                                </tr>
                                {currentRow === row.id && <ScoreSummaryExtraRows row={row} />}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>
            {!isEmpty && (
                <TablePagination
                    page={page}
                    setPage={setPage}
                    maxPage={maxPage}
                    pageSizeOpts={pageSizeOpts}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    description={paginationDescription}
                />
            )}
        </>
    );

    function renderHeading(col: Column<TRow>) {
        const { key, heading, getSort } = col;
        const canSort = getSort !== undefined;
        const isSorting = sortColumn?.key === key;

        return (
            <th
                key={key}
                className={canSort ? 'sortable' : ''}
                onClick={e => handleSortClick(e, col)}
            >
                {isSorting && sortAsc && <i className="fad fa-sort-up" />}
                {isSorting && !sortAsc && <i className="fad fa-sort-down" />}
                {heading}
            </th>
        );
    }
};

interface ScoreSummaryTableProps<T> {
    columns: Column<T>[];
    rows: T[];
    isLoading?: boolean;
    pageSizes?: number[];
    noDataMessage?: string;
    currentRow: number | null;
}

interface CustomRow extends AssessmentSummary {
    key?: number;
    id: number;
}

export default ScoreSummaryTable;
